// ============================================================================
// ENGAGEMENT PLANNER - Types & Default Data
// ============================================================================

export type EngagementDuration = '1 week' | '2 weeks' | '3 weeks' | '4 weeks' | 'custom';
export type EngagementTrack = 'rapid' | 'standard' | 'deep-dive';
export type EngagementTab = 'setup' | 'sessions' | 'calendar';

export interface AssetLink {
  id: string;
  label: string;
  url: string;
}

// Sub-items can be different types:
// - 'link': external URL only (e.g. AI Fundamentals)
// - 'portal': references existing portal functionality (e.g. Ideation, Build with AI)
// - 'campaign': form/survey campaign (e.g. Maturity Assessment, Crowd Sourcing)
// - 'session': creates co-creation sessions (e.g. Co-Creation Discovery)
// - 'placeholder': TBD functionality
export type SubItemType = 'link' | 'portal' | 'campaign' | 'session' | 'placeholder';

export interface SubItem {
  id: string;
  label: string;
  description: string;
  type: SubItemType;
  enabled: boolean;
  assets: AssetLink[];
  link?: string; // for 'link' type, external URL to navigate to
  portalTab?: string; // for 'portal' type, which sidebar tab to link to
  sessionCount?: number; // for 'session' type, how many sessions to scaffold
}

export interface EngagementPhase {
  id: string;
  name: string;
  color: string; // tailwind border color class
  colorHex: string; // for calendar cards
  icon: string; // lucide icon name
  enabled: boolean;
  subItems: SubItem[];
}

export interface Activity {
  id: string;
  name: string;
  description: string;
  durationMin: number;
  owner: string;
}

// A Discovery Session template — phases/activities for a co-creation session
export interface SessionTemplate {
  id: string;
  name: string;
  description: string;
  color: string;
  colorHex: string;
  isHalfDay: boolean;
  activities: Activity[];
  availableActivities: Activity[];
}

export interface CalendarEvent {
  id: string;
  name: string;
  color: string;
  isHalfDay: boolean;
}

export interface CalendarDay {
  date: Date;
  label: string;
  // weekIdx and dayIdx for drag-and-drop positioning
  weekIdx: number;
  dayIdx: number;
  event?: CalendarEvent;
}

export interface EngagementConfig {
  clientName: string;
  startDate: string;
  duration: EngagementDuration;
  track: EngagementTrack;
  phases: EngagementPhase[];
}

// Serializable shape for saving to backend (no Date objects)
export interface EngagementConfigSaved {
  config: Omit<EngagementConfig, 'phases'> & {
    phases: EngagementPhase[];
  };
  sessionTemplates: SessionTemplate[];
  // Calendar event placements: key = "weekIdx-dayIdx", value = CalendarEvent
  calendarEvents: Record<string, CalendarEvent>;
  planGenerated: boolean;
}

// ============================================================================
// DEFAULT PHASES — Co-Creation offering stages
// ============================================================================

export const DEFAULT_PHASES: EngagementPhase[] = [
  {
    id: 'foundations',
    name: 'Foundations',
    color: 'border-l-primary',
    colorHex: '#00c7c4',
    icon: 'flag',
    enabled: true,
    subItems: [
      {
        id: 'ai-fundamentals',
        label: 'AI Fundamentals',
        description: 'Educational briefing to debunk myths and build AI literacy.',
        type: 'link',
        link: 'https://www.version1.com/the-fundamentals-of-ai-training-course/',
        enabled: true,
        assets: [],
      },
      {
        id: 'maturity-assessment',
        label: 'AI Maturity Assessment',
        description: 'Evaluate readiness across leadership, culture, skills, and change capability.',
        type: 'campaign',
        enabled: true,
        assets: [],
      },
    ],
  },
  {
    id: 'inspiration',
    name: 'Inspiration',
    color: 'border-l-orange-500',
    colorHex: '#f97316',
    icon: 'sparkles',
    enabled: true,
    subItems: [
      {
        id: 'art-of-possible',
        label: 'Art of the Possible',
        description: 'Dynamic briefing bringing AI capabilities to life through real-world examples and demos.',
        type: 'link',
        enabled: true,
        assets: [],
      },
      {
        id: 'crowd-sourcing',
        label: 'Crowd Sourcing',
        description: 'Structured bottom-up idea gathering from across the organisation.',
        type: 'campaign',
        enabled: true,
        assets: [],
      },
      {
        id: 'ideation',
        label: 'Ideation',
        description: 'Collaborative workshop to generate and prioritise AI use cases.',
        type: 'portal',
        enabled: true,
        assets: [],
        portalTab: 'ideation',
      },
      {
        id: 'executive-envisioning',
        label: 'Executive Envisioning',
        description: 'Focused session for C-suite to align on AI vision and strategic priorities.',
        type: 'placeholder',
        enabled: false,
        assets: [],
      },
    ],
  },
  {
    id: 'discovery',
    name: 'Discovery',
    color: 'border-l-blue-500',
    colorHex: '#3b82f6',
    icon: 'search',
    enabled: true,
    subItems: [
      {
        id: 'co-creation-discovery',
        label: 'Co-Creation Discovery',
        description: 'Facilitated co-creation sessions to explore and validate use cases. Creates session templates.',
        type: 'session',
        enabled: true,
        assets: [],
        sessionCount: 2,
      },
      {
        id: 'hyper-automation',
        label: 'Hyper-Automation',
        description: 'Fast-track half-day engagement to build a demonstrable automated workflow.',
        type: 'session',
        enabled: false,
        assets: [],
        sessionCount: 1,
      },
    ],
  },
  {
    id: 'prototyping',
    name: 'Prototyping',
    color: 'border-l-purple-500',
    colorHex: '#a855f7',
    icon: 'hammer',
    enabled: true,
    subItems: [
      {
        id: 'build-with-ai',
        label: 'Build with AI',
        description: 'Generate scaffold bundles and prototypes using the Build with AI platform.',
        type: 'portal',
        enabled: true,
        assets: [],
        portalTab: 'build',
      },
    ],
  },
  {
    id: 'handover',
    name: 'Handover',
    color: 'border-l-slate-400',
    colorHex: '#94a3b8',
    icon: 'package',
    enabled: false,
    subItems: [
      {
        id: 'handover-docs',
        label: 'Documentation & Handover',
        description: 'Generate project documentation and deliverables via project tasks.',
        type: 'placeholder',
        enabled: false,
        assets: [],
      },
    ],
  },
];

// Track descriptions
export const TRACK_INFO: Record<EngagementTrack, { title: string; subtitle: string; features: string[] }> = {
  rapid: {
    title: 'Rapid',
    subtitle: 'Fast',
    features: ['Half-day sessions', 'Fast triage', 'Exec-friendly'],
  },
  standard: {
    title: 'Standard',
    subtitle: 'Optimal',
    features: ['Full-day sessions', 'Balanced depth', 'Mixed groups'],
  },
  'deep-dive': {
    title: 'Deep Dive',
    subtitle: 'Thorough',
    features: ['Multi-session', 'Research-grade', 'Practitioner-led'],
  },
};

// ============================================================================
// SESSION TEMPLATES — Discovery session agendas
// Hardcoded defaults below. These will be replaced by Phase Templates
// fetched from the platform API. Populate Phase Templates in the
// Phase Template Catalog to override these defaults.
// ============================================================================

// Fallback activities when no Phase Templates exist yet.
// Each maps to a Phase Template by convention: create Phase Templates with
// matching names/categories and they will appear in the Available Activities palette.
export const DEFAULT_SESSION_ACTIVITIES: Record<string, { activities: Activity[]; availableActivities: Activity[] }> = {
  'co-creation-discovery': {
    activities: [
      { id: 'd1', name: 'Problem Sensing', description: 'Deep dive into problem statements and pain points.', durationMin: 30, owner: 'Facilitator' },
      { id: 'd2', name: 'Problem Framing', description: 'Structure problems using How Might We format.', durationMin: 25, owner: 'Facilitator' },
      { id: 'd3', name: 'AI Solution Ideation', description: 'Generate AI-powered solution ideas for each problem.', durationMin: 30, owner: 'Facilitator + All' },
      { id: 'd4', name: 'Use Case Clustering', description: 'Group related ideas into use case candidates.', durationMin: 20, owner: 'CC Lead' },
      { id: 'd5', name: 'Impact vs Feasibility', description: 'Score and plot use cases on impact/feasibility matrix.', durationMin: 20, owner: 'CC Lead' },
      { id: 'd6', name: 'Discovery Deep Dive', description: 'Detailed analysis of top-priority use cases.', durationMin: 30, owner: 'OCTO' },
      { id: 'd7', name: 'Prioritisation & Next Steps', description: 'Final prioritisation and agreement on next steps.', durationMin: 20, owner: 'CC Lead' },
    ],
    availableActivities: [
      { id: 'da1', name: 'Persona Development', description: 'Create user personas for the selected use cases.', durationMin: 25, owner: 'Facilitator' },
      { id: 'da2', name: 'Journey Mapping', description: 'Map the current and future user journeys.', durationMin: 30, owner: 'Facilitator' },
      { id: 'da3', name: 'Technical Feasibility Review', description: 'Assess technical feasibility of top use cases.', durationMin: 20, owner: 'OCTO / Dev' },
    ],
  },
  'hyper-automation': {
    activities: [
      { id: 'h1', name: 'Use Case Briefing', description: 'Review selected use case and requirements.', durationMin: 15, owner: 'CC Lead' },
      { id: 'h2', name: 'Workflow Design', description: 'Design the automated workflow architecture.', durationMin: 30, owner: 'OCTO / Dev' },
      { id: 'h3', name: 'Live Build', description: 'Build the automated workflow using low-code/no-code tools.', durationMin: 60, owner: 'OCTO / Dev' },
      { id: 'h4', name: 'Demo & Feedback', description: 'Present the working prototype and gather feedback.', durationMin: 20, owner: 'CC Lead' },
    ],
    availableActivities: [
      { id: 'ha1', name: 'Data Preparation', description: 'Prepare sample data or synthetic datasets.', durationMin: 20, owner: 'OCTO / Dev' },
      { id: 'ha2', name: 'Iteration Sprint', description: 'Rapid iteration based on stakeholder feedback.', durationMin: 30, owner: 'OCTO / Dev' },
    ],
  },
};

const SESSION_TEMPLATE_META: Record<string, Omit<SessionTemplate, 'activities' | 'availableActivities'>> = {
  'co-creation-discovery': {
    id: 'st-discovery',
    name: 'Discovery Session',
    description: 'Structured co-creation session to explore problems, generate solutions, and validate use cases.',
    color: 'border-l-blue-500',
    colorHex: '#3b82f6',
    isHalfDay: false,
  },
  'hyper-automation': {
    id: 'st-hyper-auto',
    name: 'Hyper-Automation Session',
    description: 'Fast-track session to build and demo an automated workflow prototype.',
    color: 'border-l-blue-500',
    colorHex: '#3b82f6',
    isHalfDay: true,
  },
};

// Convert a PhaseTemplate from the API into an Activity for the agenda builder
export function phaseTemplateToActivity(pt: { phaseTemplateId: string; name: string; description: string; duration: string; category: string }): Activity {
  // Parse duration string like "30 min", "1 hour", "15-20 min" → number of minutes
  const durationMin = parseDurationToMinutes(pt.duration);
  return {
    id: `pt-${pt.phaseTemplateId}`,
    name: pt.name,
    description: pt.description,
    durationMin,
    owner: 'Facilitator',
  };
}

export function parseDurationToMinutes(duration: string): number {
  if (!duration) return 30;
  const hourMatch = duration.match(/(\d+)\s*hour/i);
  if (hourMatch) return parseInt(hourMatch[1]) * 60;
  const minMatch = duration.match(/(\d+)(?:\s*-\s*\d+)?\s*min/i);
  if (minMatch) return parseInt(minMatch[1]);
  const numMatch = duration.match(/(\d+)/);
  if (numMatch) return parseInt(numMatch[1]);
  return 30;
}

// Generate session templates from config, optionally using Phase Templates from the API
export function generateSessionTemplates(
  config: EngagementConfig,
  phaseTemplateActivities?: Activity[], // Phase Templates converted to Activities
): SessionTemplate[] {
  const templates: SessionTemplate[] = [];

  const discoveryPhase = config.phases.find(p => p.id === 'discovery');
  if (!discoveryPhase?.enabled) return templates;

  for (const sub of discoveryPhase.subItems) {
    if (!sub.enabled || sub.type !== 'session') continue;
    const meta = SESSION_TEMPLATE_META[sub.id];
    if (!meta) continue;

    // Use Phase Template activities if available, otherwise fall back to hardcoded defaults
    const defaults = DEFAULT_SESSION_ACTIVITIES[sub.id];
    const activities = phaseTemplateActivities && phaseTemplateActivities.length > 0
      ? phaseTemplateActivities
      : defaults?.activities || [];
    const availableActivities = phaseTemplateActivities && phaseTemplateActivities.length > 0
      ? [] // When using Phase Templates, all start in the agenda
      : defaults?.availableActivities || [];

    const count = sub.sessionCount || 1;
    for (let i = 0; i < count; i++) {
      templates.push({
        ...meta,
        id: `${meta.id}-${i}`,
        name: count > 1 ? `${meta.name} ${i + 1}` : meta.name,
        isHalfDay: config.track === 'rapid' ? true : meta.isHalfDay,
        activities: activities.map(a => ({ ...a, id: `${a.id}-${i}` })),
        availableActivities: availableActivities.map(a => ({ ...a, id: `${a.id}-${i}` })),
      });
    }
  }

  return templates;
}

// ============================================================================
// CALENDAR GENERATION
// ============================================================================

export function generateCalendarWeeks(config: EngagementConfig): { label: string; dateRange: string; days: CalendarDay[] }[] {
  const start = config.startDate ? new Date(config.startDate) : new Date();
  const weekCount = config.duration === '1 week' ? 1 : config.duration === '2 weeks' ? 2 : config.duration === '3 weeks' ? 3 : config.duration === '4 weeks' ? 4 : 2;

  const weeks: { label: string; dateRange: string; days: CalendarDay[] }[] = [];

  // Pre-work week (week -1)
  const preStart = new Date(start);
  preStart.setDate(preStart.getDate() - 7);
  const dayOfWeek = preStart.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  preStart.setDate(preStart.getDate() + mondayOffset);

  weeks.push({
    label: 'Pre-work (Week -1)',
    dateRange: formatDateRange(preStart),
    days: generateWeekDays(preStart, 0),
  });

  // Engagement weeks — place discovery sessions across the weeks
  const sessionTemplates = generateSessionTemplates(config);
  const engStart = new Date(start);
  const engDayOfWeek = engStart.getDay();
  const engMondayOffset = engDayOfWeek === 0 ? -6 : 1 - engDayOfWeek;
  engStart.setDate(engStart.getDate() + engMondayOffset);

  let sessionIdx = 0;
  for (let w = 0; w < weekCount; w++) {
    const weekStart = new Date(engStart);
    weekStart.setDate(weekStart.getDate() + w * 7);
    const days = generateWeekDays(weekStart, weeks.length);

    // Spread sessions across weeks — place 1-2 per week
    const sessionsThisWeek = Math.ceil((sessionTemplates.length - sessionIdx) / (weekCount - w));
    for (let s = 0; s < sessionsThisWeek && sessionIdx < sessionTemplates.length; s++) {
      const st = sessionTemplates[sessionIdx];
      // Place on different days of the week (Tue, Thu for 2; Wed for 1)
      const daySlots = sessionsThisWeek === 1 ? [2] : [1, 3]; // 0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri
      const dayIdx = daySlots[s] ?? s + 1;
      if (dayIdx < days.length) {
        days[dayIdx].event = {
          id: st.id,
          name: st.name,
          color: st.colorHex,
          isHalfDay: st.isHalfDay,
        };
      }
      sessionIdx++;
    }

    weeks.push({
      label: `Week ${w + 1}`,
      dateRange: formatDateRange(weekStart),
      days,
    });
  }

  // Post-engagement week
  const postStart = new Date(engStart);
  postStart.setDate(postStart.getDate() + weekCount * 7);
  weeks.push({
    label: 'Post-engagement',
    dateRange: formatDateRange(postStart),
    days: generateWeekDays(postStart, weeks.length),
  });

  return weeks;
}

function generateWeekDays(monday: Date, weekIdx: number): CalendarDay[] {
  const days: CalendarDay[] = [];
  for (let d = 0; d < 5; d++) {
    const date = new Date(monday);
    date.setDate(date.getDate() + d);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    days.push({
      date,
      label: `${dayNames[date.getDay()]} ${date.getDate()} ${monthNames[date.getMonth()]}`,
      weekIdx,
      dayIdx: d,
    });
  }
  return days;
}

function formatDateRange(monday: Date): string {
  const friday = new Date(monday);
  friday.setDate(friday.getDate() + 4);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monday.getDate()} ${monthNames[monday.getMonth()]} – ${friday.getDate()} ${monthNames[friday.getMonth()]}`;
}
